/**
 * Full HMLR AP1 — Change the Register — application form.
 *
 * Mirrors the official 2025 AP1 panel structure (Panels 1-14, minus the
 * now-blank Panel 8 "Third Party Notification" which HMLR retired in
 * January 2024).
 *
 * Panels:
 *   1.  Local Authority Area (the Council Tax billing authority)
 *   2.  Title Number(s) — up to 20
 *   3.  This Application Affects — Whole / Part / description
 *   4.  Application, Priority & Fees — each row: type, value, fee, +
 *       firm-wide fee method
 *   5.  Documents Lodged — name + certification (Original / Certified /
 *       Scanned) + enclosed flag
 *   6.  The Applicant — full names
 *   7.  Application Lodged By — firm / SRA / solicitor / key number /
 *       DX / phone / email
 *   8.  Address for Service — registered office / current / email
 *   9.  New Charges — lender + amount (only if a new mortgage)
 *   10. Disclosable Overriding Interests — narrative
 *   11. Confirmation of Identity — Practice Guide 67 verification
 *   12. Evidence of Identity — ID1/ID2/passport etc.
 *   13. Representation & Signature — capacity + signing solicitor
 */
import { TextField, NumberField, DateField, SelectField, YesNoField, TextAreaField, PanelGrid } from "./FormPrimitives";
import { Button } from "@/components/ui/button";
import { Plus, Trash } from "@phosphor-icons/react";

const APPLICATION_TYPES = [
  { value: "transfer_whole", label: "Transfer of whole" },
  { value: "transfer_part", label: "Transfer of part" },
  { value: "charge", label: "Registration of charge" },
  { value: "discharge", label: "Discharge (DS1 / eDS1)" },
  { value: "first_registration", label: "First registration" },
  { value: "transfer_charge", label: "Transfer of whole + Charge" },
  { value: "shared_ownership_staircasing", label: "Shared ownership staircasing" },
  { value: "change_of_name", label: "Change of name" },
  { value: "death_of_proprietor", label: "Death of proprietor" },
  { value: "restriction", label: "Application for restriction (RX1)" },
  { value: "notice", label: "Application for notice (AN1 / UN1)" },
  { value: "other", label: "Other (describe in notes)" },
];

const CERTIFICATION_OPTIONS = [
  { value: "original", label: "Original" },
  { value: "certified_conveyancer", label: "Certified by conveyancer" },
  { value: "scanned", label: "Scanned (uncertified)" },
];

const FEE_METHODS = [
  { value: "vd_account", label: "Variable Direct Debit (Key account)" },
  { value: "cheque", label: "Cheque (payable to HM Land Registry)" },
  { value: "postal_order", label: "Postal order" },
  { value: "card", label: "Card payment" },
];

const APPLICANT_CAPACITY = [
  { value: "applicant", label: "Applicant" },
  { value: "proprietor", label: "Registered proprietor" },
  { value: "personal_representative", label: "Personal representative" },
  { value: "trustee", label: "Trustee" },
  { value: "attorney", label: "Attorney (POA)" },
  { value: "executor", label: "Executor" },
];

const ADDRESS_FOR_SERVICE_TYPES = [
  { value: "property", label: "The property itself" },
  { value: "registered_office", label: "Registered office (corporate applicant)" },
  { value: "current", label: "Current address (alternative)" },
  { value: "email", label: "Email address" },
];

const ID_CONFIRMATION = [
  { value: "conveyancer_verified", label: "Verified by conveyancer (no ID1/ID2 needed)" },
  { value: "id1", label: "Form ID1 enclosed (private individual)" },
  { value: "id2", label: "Form ID2 enclosed (corporate)" },
  { value: "id5", label: "Form ID5 enclosed (digital ID — Practice Guide 67)" },
];

function emptyApplicant() {
  return { name: "", capacity: "applicant", address_1: "", postcode: "", company_number: "" };
}

function emptyApplication() {
  return { type: "transfer_whole", priority: 1, value: "", fee: "", notes: "" };
}

function emptyDocument() {
  return { name: "", certification: "original", attached: true };
}

const DEFAULT_DOCS = [
  { name: "TR1 — Transfer of whole", certification: "certified_conveyancer", attached: true },
  { name: "DS1 / e-DS1 — Discharge of mortgage", certification: "original", attached: true },
  { name: "Charge / Mortgage deed", certification: "original", attached: true },
  { name: "SDLT5 certificate", certification: "original", attached: true },
  { name: "ID1 / ID2 evidence (if applicable)", certification: "certified_conveyancer", attached: false },
];

export function defaultsAp1(caseData, item) {
  const m = item?.metadata || {};
  const applicants = (caseData?.clients || []).map((c) => ({
    name: c.name || [c.first_name, c.surname || c.last_name].filter(Boolean).join(" "),
    capacity: "applicant",
    address_1: c.address || c.address_line_1 || "",
    postcode: c.postcode || "",
    company_number: c.is_company ? c.companies_house_number || "" : "",
  }));
  return {
    // Panel 1
    local_authority: m.local_authority || caseData?.local_authority || "",
    // Panel 2
    title_numbers: m.title_numbers?.length ? m.title_numbers : (caseData?.title_number ? [caseData.title_number] : [""]),
    // Panel 3
    affects: m.affects || "whole",
    part_description: m.part_description || "",
    plan_reference: m.plan_reference || "",
    // Panel 4
    applications: m.applications?.length ? m.applications : [
      { type: "transfer_whole", priority: 1, value: caseData?.property_value || "", fee: "", notes: "" },
      ...(caseData?.mortgage_amount ? [{ type: "charge", priority: 2, value: caseData.mortgage_amount, fee: "", notes: "" }] : []),
    ],
    fee_method: m.fee_method || "vd_account",
    fee_payable_to: m.fee_payable_to || "HM Land Registry",
    // Panel 5
    documents: m.documents?.length ? m.documents : DEFAULT_DOCS,
    // Panel 6
    applicants: m.applicants?.length ? m.applicants : (applicants.length ? applicants : [emptyApplicant()]),
    // Panel 7 — lodging firm
    matter_reference: m.matter_reference || caseData?.reference || "",
    key_number: m.key_number || "",
    lodging_firm_name: m.lodging_firm_name || "UKC Legal Solicitors",
    lodging_sra_number: m.lodging_sra_number || "",
    lodging_solicitor_name: m.lodging_solicitor_name || caseData?.solicitor_name || "",
    lodging_address: m.lodging_address || "38 Prince of Wales Road, Norwich NR1 1LG",
    lodging_dx: m.lodging_dx || "",
    lodging_phone: m.lodging_phone || "01603 339174",
    lodging_email: m.lodging_email || "",
    // Panel 8 — address for service
    address_for_service_type: m.address_for_service_type || "property",
    address_for_service_text: m.address_for_service_text || "",
    address_for_service_email: m.address_for_service_email || "",
    // Panel 9 — new charges (if any)
    new_charge: m.new_charge || (caseData?.mortgage_amount ? "yes" : "no"),
    lender_name: m.lender_name || caseData?.lender_name || "",
    lender_address: m.lender_address || "",
    charge_amount: m.charge_amount || caseData?.mortgage_amount || "",
    electronic_signature: m.electronic_signature || "no",
    // Panel 10 — DOI
    disclosable_overriding_interests: m.disclosable_overriding_interests || "",
    // Panel 11 — confirmation of identity
    identity_confirmation: m.identity_confirmation || "conveyancer_verified",
    identity_notes: m.identity_notes || "",
    // Panel 12 — evidence
    evidence_passport: m.evidence_passport || "no",
    evidence_driving_licence: m.evidence_driving_licence || "no",
    evidence_other: m.evidence_other || "",
    // Panel 13 — declaration
    signing_capacity: m.signing_capacity || "Conveyancer for the applicant",
    signing_name: m.signing_name || caseData?.solicitor_name || "",
    signing_date: m.signing_date || todayIso(),
    additional_notes: m.additional_notes || "",
  };
}

export function validateAp1(v) {
  const errs = {};
  if (!v.local_authority) errs.local_authority = "Required — the Council Tax billing authority";
  const titles = (v.title_numbers || []).filter(Boolean);
  if (!titles.length) errs.title_numbers = "At least 1 title number required";
  if (v.affects === "part" && !v.part_description) errs.part_description = "Describe the part (or attach a plan)";
  if (!(v.applications || []).length) errs.applications = "At least 1 application required";
  (v.applications || []).forEach((a, i) => {
    if (!a.value || Number(a.value) < 0) errs[`application_${i}_value`] = "Enter consideration or market value";
    if (a.fee === "" || Number(a.fee) < 0) errs[`application_${i}_fee`] = "Enter fee";
  });
  if (!(v.applicants || []).length) errs.applicants = "At least 1 applicant required";
  (v.applicants || []).forEach((a, i) => { if (!a.name) errs[`applicant_${i}_name`] = "Required"; });
  if (!v.matter_reference) errs.matter_reference = "Required";
  if (!v.lodging_solicitor_name) errs.lodging_solicitor_name = "Required";
  if (v.address_for_service_type === "email" && !v.address_for_service_email) errs.address_for_service_email = "Required";
  if (v.address_for_service_type === "current" && !v.address_for_service_text) errs.address_for_service_text = "Required";
  if (v.new_charge === "yes") {
    if (!v.lender_name) errs.lender_name = "Required when new charge";
    if (!v.charge_amount || Number(v.charge_amount) <= 0) errs.charge_amount = "Required when new charge";
  }
  if (!v.signing_name) errs.signing_name = "Required";
  return errs;
}

function todayIso() { return new Date().toISOString().slice(0, 10); }

// ---- Repeaters ----

function TitleNumberList({ list, set, error }) {
  const update = (i, val) => set(list.map((t, idx) => idx === i ? val : t));
  const remove = (i) => set(list.filter((_, idx) => idx !== i));
  const add = () => list.length < 20 && set([...list, ""]);
  return (
    <div className="sm:col-span-3 space-y-2">
      <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-600">
        Title number(s) <span className="text-rose-600">*</span>
        <span className="text-slate-400 normal-case font-normal ml-1">(up to 20)</span>
      </div>
      {list.map((t, i) => (
        <div key={i} className="flex items-center gap-2">
          <input
            value={t}
            onChange={(e) => update(i, e.target.value.toUpperCase())}
            placeholder={i === 0 ? "e.g. NK123456" : ""}
            className="h-9 px-3 text-sm font-mono flex-1 border border-slate-300 rounded-md"
            data-testid={`field-title-${i}`}
          />
          {list.length > 1 && (
            <Button size="sm" variant="ghost" onClick={() => remove(i)} className="text-rose-600 h-9" data-testid={`title-remove-${i}`}>
              <Trash weight="bold" className="w-4 h-4" />
            </Button>
          )}
        </div>
      ))}
      {list.length < 20 && (
        <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="ap1-add-title">
          <Plus weight="bold" className="w-3.5 h-3.5 mr-1" /> Add another title (max 20)
        </Button>
      )}
      {error && <p className="text-[11px] text-rose-600">{error}</p>}
    </div>
  );
}

function ApplicantEditor({ a, idx, onChange, onRemove, errors }) {
  const set = (k, v) => onChange({ ...a, [k]: v });
  return (
    <div className="border border-slate-200 rounded-lg p-3 space-y-3 bg-slate-50/40" data-testid={`applicant-${idx}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Applicant #{idx + 1}</div>
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-600 h-7 px-2" data-testid={`applicant-remove-${idx}`}>
            <Trash weight="bold" className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
      <PanelGrid>
        <TextField name={`applicant_${idx}_name`} label="Full name" required value={a.name} onChange={(x) => set("name", x)} error={errors[`applicant_${idx}_name`]} span={2} />
        <SelectField name={`applicant_${idx}_capacity`} label="Capacity" value={a.capacity} onChange={(x) => set("capacity", x)} options={APPLICANT_CAPACITY} />
        <TextField name={`applicant_${idx}_address_1`} label="Address" value={a.address_1} onChange={(x) => set("address_1", x)} span={2} />
        <TextField name={`applicant_${idx}_postcode`} label="Postcode" value={a.postcode} onChange={(x) => set("postcode", x)} />
        <TextField name={`applicant_${idx}_company_number`} label="Company number" hint="If corporate applicant" value={a.company_number} onChange={(x) => set("company_number", x)} />
      </PanelGrid>
    </div>
  );
}

function ApplicationRow({ a, idx, onChange, onRemove, canRemove, errors }) {
  const set = (k, v) => onChange({ ...a, [k]: v });
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-slate-50/40 space-y-2" data-testid={`application-${idx}`}>
      <div className="grid grid-cols-12 gap-2 items-end">
        <div className="col-span-1">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Priority</div>
          <input type="number" min={1} value={a.priority} onChange={(e) => set("priority", Number(e.target.value))} className="h-9 w-full text-sm text-center border border-slate-300 rounded-md" data-testid={`field-application_${idx}_priority`} />
        </div>
        <div className="col-span-5">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Application type</div>
          <select value={a.type} onChange={(e) => set("type", e.target.value)} className="h-9 w-full text-sm border border-slate-300 rounded-md px-2" data-testid={`field-application_${idx}_type`}>
            {APPLICATION_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="col-span-3">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Value / Market value (£)</div>
          <input type="number" step="0.01" value={a.value} onChange={(e) => set("value", e.target.value)} className={`h-9 w-full text-sm border rounded-md px-2 ${errors[`application_${idx}_value`] ? "border-rose-400" : "border-slate-300"}`} data-testid={`field-application_${idx}_value`} />
        </div>
        <div className="col-span-2">
          <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">Fee (£)</div>
          <input type="number" step="0.01" value={a.fee} onChange={(e) => set("fee", e.target.value)} className={`h-9 w-full text-sm border rounded-md px-2 ${errors[`application_${idx}_fee`] ? "border-rose-400" : "border-slate-300"}`} data-testid={`field-application_${idx}_fee`} />
        </div>
        <div className="col-span-1 text-right">
          {canRemove && (
            <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-600 h-9 px-2" data-testid={`application-remove-${idx}`}>
              <Trash weight="bold" className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <input value={a.notes || ""} onChange={(e) => set("notes", e.target.value)} placeholder="Notes (optional — describe non-standard applications, e.g. assent, plan ref, etc.)" className="h-8 w-full text-xs border border-slate-200 rounded-md px-2" data-testid={`field-application_${idx}_notes`} />
    </div>
  );
}

function DocRow({ d, idx, onChange, onRemove }) {
  const set = (k, v) => onChange({ ...d, [k]: v });
  return (
    <div className="grid grid-cols-12 gap-2 items-center border border-slate-200 rounded-md p-2 bg-slate-50/40" data-testid={`ap1-doc-${idx}`}>
      <div className="col-span-6">
        <input value={d.name} onChange={(e) => set("name", e.target.value)} placeholder="Document name (e.g. TR1, DS1, ID1, Probate)" className="h-8 w-full text-sm border border-slate-300 rounded-md px-2" data-testid={`field-doc_${idx}_name`} />
      </div>
      <div className="col-span-3">
        <select value={d.certification} onChange={(e) => set("certification", e.target.value)} className="h-8 w-full text-sm border border-slate-300 rounded-md px-2" data-testid={`field-doc_${idx}_cert`}>
          {CERTIFICATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      <div className="col-span-2">
        <label className="inline-flex items-center gap-2 text-xs">
          <input type="checkbox" checked={d.attached} onChange={(e) => set("attached", e.target.checked)} data-testid={`field-doc_${idx}_attached`} />
          Enclosed
        </label>
      </div>
      <div className="col-span-1 text-right">
        <Button size="sm" variant="ghost" onClick={onRemove} className="text-rose-600 h-8 px-2" data-testid={`doc-remove-${idx}`}>
          <Trash weight="bold" className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
}

// ---- Panels ----

export function Ap1PanelLA({ v, set, e }) {
  return (
    <PanelGrid>
      <TextField name="local_authority" label="Local Authority Area (Council Tax)" required hint="The council to which the Council Tax for this property is paid" value={v.local_authority} onChange={(x) => set("local_authority", x)} error={e.local_authority} span={2} />
    </PanelGrid>
  );
}

export function Ap1PanelTitles({ v, set, e }) {
  return (
    <PanelGrid>
      <TitleNumberList list={v.title_numbers || [""]} set={(x) => set("title_numbers", x)} error={e.title_numbers} />
    </PanelGrid>
  );
}

export function Ap1PanelAffects({ v, set, e }) {
  return (
    <PanelGrid>
      <SelectField name="affects" label="This application affects" required value={v.affects} onChange={(x) => set("affects", x)} options={[
        { value: "whole", label: "The whole of the registered title" },
        { value: "part", label: "Part of the registered title (plan required)" },
      ]} />
      {v.affects === "part" && (
        <>
          <TextField name="part_description" label="Description of part" required value={v.part_description} onChange={(x) => set("part_description", x)} error={e.part_description} hint="e.g. land edged red on attached plan" span={2} />
          <TextField name="plan_reference" label="Plan reference / number" value={v.plan_reference} onChange={(x) => set("plan_reference", x)} />
        </>
      )}
    </PanelGrid>
  );
}

export function Ap1PanelApplications({ v, set, e }) {
  const list = v.applications || [];
  const update = (i, a) => set("applications", list.map((p, idx) => idx === i ? a : p));
  const remove = (i) => set("applications", list.filter((_, idx) => idx !== i));
  const add = () => set("applications", [...list, { ...emptyApplication(), priority: list.length + 1 }]);
  return (
    <div className="space-y-2 py-3" data-testid="ap1-applications-panel">
      <p className="text-xs text-slate-500">
        Enter each application in priority order. <strong>Value</strong> is the amount paid — if no money changes hands (e.g. gift, assent),
        enter the <strong>full market value</strong>. <strong>Fee</strong> is the HMLR scale fee for that application.
      </p>
      {list.map((a, i) => (
        <ApplicationRow key={i} a={a} idx={i} onChange={(na) => update(i, na)} onRemove={() => remove(i)} canRemove={list.length > 1} errors={e} />
      ))}
      <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="ap1-add-application">
        <Plus weight="bold" className="w-4 h-4 mr-1" /> Add another application
      </Button>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t border-slate-100">
        <SelectField name="fee_method" label="Fee payment method" value={v.fee_method} onChange={(x) => set("fee_method", x)} options={FEE_METHODS} />
        <TextField name="fee_payable_to" label="Cheque payable to" value={v.fee_payable_to} onChange={(x) => set("fee_payable_to", x)} hint="Always 'HM Land Registry'" />
      </div>
    </div>
  );
}

export function Ap1PanelDocuments({ v, set }) {
  const list = v.documents || [];
  const update = (i, d) => set("documents", list.map((p, idx) => idx === i ? d : p));
  const remove = (i) => set("documents", list.filter((_, idx) => idx !== i));
  const add = () => set("documents", [...list, emptyDocument()]);
  return (
    <div className="space-y-2 py-3" data-testid="ap1-documents-panel">
      <p className="text-xs text-slate-500">
        List every document enclosed or uploaded. Pick the correct certification — HMLR requisitions are common where a certification is wrong.
      </p>
      {list.map((d, i) => (
        <DocRow key={i} d={d} idx={i} onChange={(nd) => update(i, nd)} onRemove={() => remove(i)} />
      ))}
      <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="ap1-add-doc">
        <Plus weight="bold" className="w-4 h-4 mr-1" /> Add another document
      </Button>
    </div>
  );
}

export function Ap1PanelApplicants({ v, set, e }) {
  const list = v.applicants || [];
  const update = (i, a) => set("applicants", list.map((p, idx) => idx === i ? a : p));
  const remove = (i) => set("applicants", list.filter((_, idx) => idx !== i));
  const add = () => set("applicants", [...list, emptyApplicant()]);
  return (
    <div className="space-y-3 py-3" data-testid="ap1-applicants-panel">
      <p className="text-xs text-slate-500">The full name(s) of the person(s) actually applying to change the register.</p>
      {list.map((a, i) => (
        <ApplicantEditor key={i} a={a} idx={i} onChange={(na) => update(i, na)} onRemove={list.length > 1 ? () => remove(i) : null} errors={e} />
      ))}
      <Button size="sm" variant="outline" onClick={add} className="border-dashed" data-testid="ap1-add-applicant">
        <Plus weight="bold" className="w-4 h-4 mr-1" /> Add another applicant
      </Button>
    </div>
  );
}

export function Ap1PanelLodgingFirm({ v, set, e }) {
  return (
    <PanelGrid>
      <TextField name="matter_reference" label="Our reference" required value={v.matter_reference} onChange={(x) => set("matter_reference", x)} error={e.matter_reference} />
      <TextField name="key_number" label="HMLR Key Number" hint="VD account number" value={v.key_number} onChange={(x) => set("key_number", x)} />
      <TextField name="lodging_firm_name" label="Firm name" value={v.lodging_firm_name} onChange={(x) => set("lodging_firm_name", x)} />
      <TextField name="lodging_sra_number" label="SRA / CLC number" value={v.lodging_sra_number} onChange={(x) => set("lodging_sra_number", x)} />
      <TextField name="lodging_solicitor_name" label="Solicitor name" required value={v.lodging_solicitor_name} onChange={(x) => set("lodging_solicitor_name", x)} error={e.lodging_solicitor_name} />
      <TextField name="lodging_dx" label="DX number" value={v.lodging_dx} onChange={(x) => set("lodging_dx", x)} />
      <TextField name="lodging_phone" label="Phone" value={v.lodging_phone} onChange={(x) => set("lodging_phone", x)} />
      <TextField name="lodging_email" label="Email" type="email" value={v.lodging_email} onChange={(x) => set("lodging_email", x)} />
      <TextField name="lodging_address" label="Postal address" value={v.lodging_address} onChange={(x) => set("lodging_address", x)} span={3} />
    </PanelGrid>
  );
}

export function Ap1PanelAddressForService({ v, set, e }) {
  return (
    <PanelGrid>
      <SelectField name="address_for_service_type" label="Address for service" required value={v.address_for_service_type} onChange={(x) => set("address_for_service_type", x)} options={ADDRESS_FOR_SERVICE_TYPES} hint="HMLR will send all correspondence here" />
      {v.address_for_service_type === "email" && (
        <TextField name="address_for_service_email" label="Email for service" type="email" required value={v.address_for_service_email} onChange={(x) => set("address_for_service_email", x)} error={e.address_for_service_email} span={2} />
      )}
      {(v.address_for_service_type === "current" || v.address_for_service_type === "registered_office") && (
        <TextAreaField name="address_for_service_text" label="Address" required={v.address_for_service_type === "current"} rows={2} value={v.address_for_service_text} onChange={(x) => set("address_for_service_text", x)} error={e.address_for_service_text} span={2} />
      )}
    </PanelGrid>
  );
}

export function Ap1PanelNewCharges({ v, set, e }) {
  return (
    <PanelGrid>
      <YesNoField name="new_charge" label="Registering a new charge?" value={v.new_charge} onChange={(x) => set("new_charge", x)} />
      {v.new_charge === "yes" && (
        <>
          <TextField name="lender_name" label="Lender / chargee" required value={v.lender_name} onChange={(x) => set("lender_name", x)} error={e.lender_name} span={2} />
          <TextField name="lender_address" label="Lender address" value={v.lender_address} onChange={(x) => set("lender_address", x)} span={2} />
          <NumberField name="charge_amount" label="Amount secured (£)" required value={v.charge_amount} onChange={(x) => set("charge_amount", x)} error={e.charge_amount} />
          <YesNoField name="electronic_signature" label="Deed electronically signed?" value={v.electronic_signature} onChange={(x) => set("electronic_signature", x)} />
        </>
      )}
    </PanelGrid>
  );
}

export function Ap1PanelDOI({ v, set }) {
  return (
    <PanelGrid>
      <TextAreaField
        name="disclosable_overriding_interests"
        label="Disclosable Overriding Interests"
        rows={3}
        hint="Any interests that override the register (e.g. unregistered leases under 7 years, rights of way, easements not noted). Leave blank if none."
        value={v.disclosable_overriding_interests}
        onChange={(x) => set("disclosable_overriding_interests", x)}
      />
    </PanelGrid>
  );
}

export function Ap1PanelIdentity({ v, set }) {
  return (
    <PanelGrid>
      <SelectField name="identity_confirmation" label="Confirmation of Identity" required hint="Per Practice Guide 67" value={v.identity_confirmation} onChange={(x) => set("identity_confirmation", x)} options={ID_CONFIRMATION} span={2} />
      <YesNoField name="evidence_passport" label="Passport copy enclosed?" value={v.evidence_passport} onChange={(x) => set("evidence_passport", x)} />
      <YesNoField name="evidence_driving_licence" label="Driving licence copy enclosed?" value={v.evidence_driving_licence} onChange={(x) => set("evidence_driving_licence", x)} />
      <TextField name="evidence_other" label="Other ID evidence" hint="e.g. utility bill, Thirdfort reference" value={v.evidence_other} onChange={(x) => set("evidence_other", x)} />
      <TextAreaField name="identity_notes" label="Identity notes" rows={2} value={v.identity_notes} onChange={(x) => set("identity_notes", x)} />
    </PanelGrid>
  );
}

export function Ap1PanelDeclaration({ v, set, e }) {
  return (
    <PanelGrid>
      <TextField name="signing_capacity" label="Capacity of signer" value={v.signing_capacity} onChange={(x) => set("signing_capacity", x)} hint="e.g. Conveyancer for the applicant" span={2} />
      <TextField name="signing_name" label="Name of signing solicitor" required value={v.signing_name} onChange={(x) => set("signing_name", x)} error={e.signing_name} />
      <DateField name="signing_date" label="Date" value={v.signing_date} onChange={(x) => set("signing_date", x)} />
      <TextAreaField name="additional_notes" label="Additional notes / cover-letter content" value={v.additional_notes} onChange={(x) => set("additional_notes", x)} />
    </PanelGrid>
  );
}

export function ap1Panels() {
  return [
    { id: "la", label: "1. Local Authority Area", Component: Ap1PanelLA, fields: ["local_authority"], requiredFields: ["local_authority"] },
    { id: "titles", label: "2. Title Number(s)", Component: Ap1PanelTitles, fields: ["title_numbers"], requiredFields: ["title_numbers"] },
    { id: "affects", label: "3. This application affects", Component: Ap1PanelAffects, fields: ["affects", "part_description"], requiredFields: ["affects"] },
    { id: "applications", label: "4. Application, priority & fees", Component: Ap1PanelApplications, fields: ["applications", "fee_method"], requiredFields: ["applications"] },
    { id: "documents", label: "5. Documents lodged", Component: Ap1PanelDocuments, fields: ["documents"], requiredFields: [] },
    { id: "applicants", label: "6. The applicant(s)", Component: Ap1PanelApplicants, fields: ["applicants"], requiredFields: ["applicants"] },
    { id: "lodging", label: "7. Application lodged by", Component: Ap1PanelLodgingFirm, fields: ["matter_reference", "lodging_firm_name", "lodging_solicitor_name", "key_number", "lodging_phone", "lodging_email"], requiredFields: ["matter_reference", "lodging_solicitor_name"] },
    { id: "service", label: "8. Address for service", Component: Ap1PanelAddressForService, fields: ["address_for_service_type"], requiredFields: ["address_for_service_type"] },
    { id: "new_charges", label: "9. New charges", Component: Ap1PanelNewCharges, fields: ["new_charge"], requiredFields: ["new_charge"] },
    { id: "doi", label: "10. Disclosable overriding interests", Component: Ap1PanelDOI, fields: ["disclosable_overriding_interests"], requiredFields: [] },
    { id: "identity", label: "11. Confirmation of identity", Component: Ap1PanelIdentity, fields: ["identity_confirmation"], requiredFields: ["identity_confirmation"] },
    { id: "declaration", label: "12. Representation & signature", Component: Ap1PanelDeclaration, fields: ["signing_name", "signing_date", "signing_capacity"], requiredFields: ["signing_name"] },
  ];
}
